import React from "react";
import {stepOptions} from "./StepOptions";
import {apiAction} from "@arboxappv4/shared/src/helpers/HTTP";
import {store} from "@arboxappv4/shared/src/redux/store";
import {setLoadingType} from "@arboxappv4/shared/src/redux/siteSlice";
import { courseBookingAllowed } from "../helpers/functions";
import {
    DB_DATE_FORMAT,
    LOADING_PAGE,
    DB_TIME_FORMAT,
    LOADING_PAYMENT
} from "@arboxappv4/shared/src/constants/constants";
import {cleanStepper, updateError, updateFlags, updateFlowRes} from "@arboxappv4/shared/src/redux/stepperSlice";
import {t} from "i18next";
import {
    addClarityInfo,
    callSiteLog,
    getBookingCtaText,
    getBoxPageOptions, getRelevantStore,
    handleMembershipStartDate, handleRecordSessionOnFlowError,
    onLinkClicked,
    sortCourseEventDays
} from "../helpers/functions";
import {addTokenAfterRegister} from "@arboxappv4/shared/src/redux/authSlice";
import MembershipDescription from "./steps/directPageComponents/MembershipDescription";
import MembershipInfo from "./steps/directPageComponents/MembershipInfo";
import dayjs from "dayjs";
import {
    openModal,
    SELECT_QUANTITY_MODAL,
    updateModalRes
} from "@arboxappv4/shared/src/redux/modalManagerSlice";
import {fetchUserMembershipsByBox} from "@arboxappv4/shared/src/redux/userSlice";
import {
    bookingTypes,
    membershipTypes, paymentErrorCatchLog, paymentLogGroup,
    projectTypes,
    sessionTypes
} from "@arboxappv4/shared/src/helpers/constants";
import GroupSessionDescription from "./steps/directPageComponents/GroupSessionDescription";
import GroupSessionInfo from "./steps/directPageComponents/GroupSessionInfo";
import CourseDescription from "./steps/directPageComponents/CourseDescription";
import CourseInfo from "./steps/directPageComponents/CourseInfo";
import {pixelEvents, sendPixelEvent} from "../helpers/hooks/UsePixel";
import {setKioskError, setKioskLoadingType} from "@arboxappv4/shared/src/redux/kioskStandAlone/kioskSlice";
import uuid from 'react-uuid';
import {GROUP_FORM_SETTINGS, HUGIM_FORM_SETTINGS} from "../../../src/constants";
import {getGroupFormId, getHugFormId, getTrialFlowFormId} from "../helpers/FlowFormHelpers";

export const flowOptions = {
    REGISTER: 'REGISTER',
    MEMBERSHIP_PURCHASE: 'MEMBERSHIP_PURCHASE',
    DIRECT_MEMBERSHIP_PURCHASE: 'DIRECT_MEMBERSHIP_PURCHASE',
    WAIVER: 'WAIVER',
    GROUP_SESSION: 'GROUP_SESSION',
    DIRECT_GROUP_SESSION: 'DIRECT_GROUP_SESSION',
    STAFF_APPOINTMENT: 'STAFF_APPOINTMENT',
    SPACE_APPOINTMENT: 'SPACE_APPOINTMENT',
    DIRECT_STAFF_APPOINTMENT: 'DIRECT_STAFF_APPOINTMENT',
    DIRECT_SPACE_APPOINTMENT: 'DIRECT_SPACE_APPOINTMENT',
    COURSE: 'COURSE',
    DIRECT_COURSE: 'DIRECT_COURSE',
    PRODUCT_PURCHASE: 'PRODUCT_PURCHASE',
    DIRECT_PRODUCT_PURCHASE: 'DIRECT_PRODUCT_PURCHASE',
    DIRECT_TRIAL_GROUP: 'DIRECT_TRIAL_GROUP',
    LEAD_FORM: 'LEAD_FORM',
    KIOSK_CLOSE_DEBT: 'KIOSK_CLOSE_DEBT',
    KIOSK_ARRIVAL_CONFIRMATION: 'KIOSK_ARRIVAL_CONFIRMATION',
    KIOSK_CLOSE_ALL_DEBTS: 'KIOSK_CLOSE_ALL_DEBTS'
}

//<editor-fold desc="init flow">

export const initFlow = ({flowName, initProps, continueFlow}) => {
    callSiteLog('initFlow', {flowName, initProps, continueFlow, route: window.location.href}, paymentLogGroup)
    switch(flowName) {
        case flowOptions.REGISTER:
            return initRegister({continueFlow});
        case flowOptions.MEMBERSHIP_PURCHASE:
            return initMembershipPurchase({continueFlow});
        case flowOptions.DIRECT_MEMBERSHIP_PURCHASE:
            return initDirectMembershipPurchase({continueFlow});
        case flowOptions.WAIVER:
            return { flowName: flowOptions.WAIVER, steps: [stepOptions.WAIVER], currStep: stepOptions.WAIVER};
        case flowOptions.GROUP_SESSION:
        case flowOptions.DIRECT_GROUP_SESSION:
            return initGroupSession({flowName, continueFlow})
        case flowOptions.STAFF_APPOINTMENT:
        case flowOptions.SPACE_APPOINTMENT:
            return initAppointment({flowName, continueFlow})
        case flowOptions.DIRECT_STAFF_APPOINTMENT:
        case flowOptions.DIRECT_SPACE_APPOINTMENT:
            return initDirectAppointment({flowName, continueFlow})
        case flowOptions.COURSE:
        case flowOptions.DIRECT_COURSE:
            return initCourse({flowName, continueFlow})
        case flowOptions.PRODUCT_PURCHASE:
        case flowOptions.DIRECT_PRODUCT_PURCHASE:
            return initProductPurchase({flowName, continueFlow})
        case flowOptions.DIRECT_TRIAL_GROUP:
            return initDirectTrialGroup(initProps)
        case flowOptions.LEAD_FORM:
            return { flowName: flowOptions.LEAD_FORM, steps: [stepOptions.LEAD_FORM], currStep: stepOptions.LEAD_FORM };
        case flowOptions.KIOSK_CLOSE_DEBT:
            return { flowName: flowOptions.KIOSK_CLOSE_DEBT, steps: [stepOptions.PAYMENT], currStep: stepOptions.PAYMENT };
        case flowOptions.KIOSK_ARRIVAL_CONFIRMATION:
            return { flowName: flowOptions.KIOSK_ARRIVAL_CONFIRMATION, steps: [stepOptions.ARRIVAL_CONFIRMATION], currStep: stepOptions.ARRIVAL_CONFIRMATION };
        case flowOptions.KIOSK_CLOSE_ALL_DEBTS:
            return { flowName: flowOptions.KIOSK_CLOSE_ALL_DEBTS, steps: [stepOptions.PAYMENT], currStep: stepOptions.PAYMENT };

        default:
            return {};
    }
}

const initRegister = ({continueFlow}) => {
    const flags = store.getState().stepper.flags
    const box = store.getState().site.box
    const hasWaiver = !!box?.digital_form_id
    const hasPayment = !flags?.continueWithoutMembership
    let steps;
    if(flags.fromMembershipSignIn) {
        steps = [stepOptions.PERSONAL_DETAILS]
    } else {
        steps = [stepOptions.PERSONAL_DETAILS, stepOptions.SELECT_MEMBERSHIP]
    }
    if(hasWaiver) {
        steps.push(stepOptions.WAIVER)
    }
    if(hasPayment) {
        steps.push(stepOptions.PAYMENT)
    }
    if(continueFlow) return {steps: steps}
    return {
        flowName: flowOptions.REGISTER,
        steps: steps,
        currStep: stepOptions.PERSONAL_DETAILS,
    }
}

const initMembershipPurchase = ({continueFlow}) => {
    const loggedIn = store.getState().auth.loggedIn
    if(continueFlow === flowOptions.MEMBERSHIP_PURCHASE) {
        let newSteps
        if(loggedIn) {
            newSteps = [stepOptions.SELECT_MEMBERSHIP, stepOptions.PAYMENT]
        } //new users are redirected to "register" route through handleAsyncChecksAfterSignIn, so they won't arrive here
        return { steps: newSteps }
    } else {
        return {
            flowName: flowOptions.MEMBERSHIP_PURCHASE,
            steps: loggedIn ? [stepOptions.SELECT_MEMBERSHIP, stepOptions.PAYMENT] : [stepOptions.SELECT_MEMBERSHIP],
            currStep: stepOptions.SELECT_MEMBERSHIP,
            flags: {}
        }
    }
}

const initDirectMembershipPurchase = ({continueFlow}) => {
    const loggedIn = store.getState().auth.loggedIn
    if(continueFlow === flowOptions.DIRECT_MEMBERSHIP_PURCHASE) {
        let newSteps
        if(loggedIn) {
            newSteps = [stepOptions.DIRECT_ITEM, stepOptions.PAYMENT]
        } //new users are redirected to "register" route through handleAsyncChecksAfterSignIn, so they won't arrive here
        return { steps: newSteps }
    } else {
        return {
            flowName: flowOptions.DIRECT_MEMBERSHIP_PURCHASE,
            steps: loggedIn ? [stepOptions.DIRECT_ITEM, stepOptions.PAYMENT] : [stepOptions.DIRECT_ITEM],
            currStep: stepOptions.DIRECT_ITEM,
            flags: {}
        }
    }
}

const initGroupSession = ({flowName, continueFlow}) => {
    const loggedIn = store.getState().auth.loggedIn
    if(continueFlow === flowName) {
        let newSteps = [stepOptions.GROUP_SESSIONS]
        const groupForm = getGroupFormId()
        if(!loggedIn) {
            newSteps.push(stepOptions.PERSONAL_DETAILS)
        }
        if(groupForm) {
            newSteps.push(stepOptions.WAIVER)
        }
        newSteps.push(stepOptions.PAYMENT)
        return { steps: newSteps }
    } else {
        return {
            flowName: flowName,
            steps: [stepOptions.GROUP_SESSIONS],
            currStep: stepOptions.GROUP_SESSIONS,
            flags: {}
        }
    }
}

const initDirectTrialGroup = (initProps) => {
    const box = store.getState().site.box
    const loggedIn = store.getState().auth.loggedIn
    const target = initProps?.target;
    const isHug = target?.box_categories?.category_type?.name === sessionTypes.HUGIM
    let formId
    if(isHug) {
        formId = getTrialFlowFormId(HUGIM_FORM_SETTINGS)
    } else { //right now this means it must be class type because there is no other trial types
        formId = getTrialFlowFormId(GROUP_FORM_SETTINGS)
    }
    const steps = formId ? [stepOptions.TRIAL_GROUP_DETAILS, stepOptions.WAIVER, stepOptions.PAYMENT] : [stepOptions.TRIAL_GROUP_DETAILS, stepOptions.PAYMENT]

    return {
        flowName: flowOptions.DIRECT_TRIAL_GROUP,
        steps: steps,
        currStep: stepOptions.TRIAL_GROUP_DETAILS,
        flags: {loggedIn}
    }
}

const initAppointment = ({flowName, continueFlow}) => {
    const loggedIn = store.getState().auth.loggedIn
    const box = store.getState().site.box
    const hasGroupStep = box?.boxes_settings?.some(setting => setting.prop_name === 'appointmentShowGroupStep' && setting.properties && setting.properties.active) ?? false
    const extraStepsBefore = hasGroupStep ? [stepOptions.GROUP_SERVICE_TYPE] : []
    let steps = [...extraStepsBefore, stepOptions.SERVICE_TYPE, stepOptions.AVAILABILITY_SLOT]
    if(continueFlow === flowName) {
        let newSteps
        if(loggedIn) {
            newSteps = [...steps, stepOptions.PAYMENT]
        } else {
            newSteps = box?.digital_form_id ? [...steps, stepOptions.PERSONAL_DETAILS, stepOptions.WAIVER, stepOptions.PAYMENT] : [...steps, stepOptions.PERSONAL_DETAILS, stepOptions.PAYMENT]
        }
        return { steps: newSteps }
    } else {
        return {
            flowName: flowName,
            steps: steps,
            currStep: steps[0],
            flags: {serviceReady: false}
        }
    }
}

const initDirectAppointment = ({flowName, continueFlow}) => {
    const loggedIn = store.getState().auth.loggedIn
    const box = store.getState().site.box
    let steps = [stepOptions.AVAILABILITY_SLOT]
    if(continueFlow === flowName) {
        let newSteps
        if(loggedIn) {
            newSteps = [...steps, stepOptions.PAYMENT]
        } else {
            newSteps = box?.digital_form_id ? [...steps, stepOptions.PERSONAL_DETAILS, stepOptions.WAIVER, stepOptions.PAYMENT] : [...steps, stepOptions.PERSONAL_DETAILS, stepOptions.PAYMENT]
        }
        return { steps: newSteps }
    } else {
        return {
            flowName: flowName,
            steps: steps,
            currStep: steps[0],
            flags: {}
        }
    }
}

const initCourse = ({flowName, continueFlow}) => {
    if(continueFlow === flowName) {
        return { steps: groupRegistrationCourseSteps() }
    } else {
        return {
            flowName: flowName,
            steps: [stepOptions.COURSES],
            currStep: stepOptions.COURSES,
            flags: {serviceReady: false, courseInfo: null, isChildRegistration: false, isNewChild: false, membershipTypeSelection: null}
        }
    }
}

export const groupRegistrationCourseSteps = () => {
    const loggedIn = store.getState().auth.loggedIn
    const userBox = store.getState().user.activeUsersBoxes
    const flags = store.getState().stepper.flags
    const hugForm = getHugFormId()
    let steps = [stepOptions.COURSES]

    if(flags.isChildRegistration) {
        if (loggedIn) {
            const allowedToGroupRegister = courseBookingAllowed(userBox, {register_group_member: true})
            if(!allowedToGroupRegister) {
                window.location.reload()
                return
            }
            //If user is head of a group and the group has at one user besides the head
            const children = userBox.group_connection?.group_members ? userBox.group_connection.group_members.filter(connection => connection.id !== userBox.group_connection?.users_boxes_id) : []
            if (userBox.group_connection?.head && children.length > 0) {
                steps.push(stepOptions.COURSE_CHILD_SELECTION)
            } else {
                steps.push(stepOptions.COURSE_CHILD_DETAILS)
            }
        } else {
            steps = [...steps, stepOptions.COURSE_PARENT_DETAILS, stepOptions.COURSE_CHILD_DETAILS]
        }
    } else {
        if(!loggedIn) {
            steps.push(stepOptions.PERSONAL_DETAILS)
        }
    }
    if(hugForm) {
        steps.push(stepOptions.WAIVER)
    }
    steps.push(stepOptions.PAYMENT)

    return steps
}

const initProductPurchase = ({flowName, continueFlow}) => {
    const loggedIn = store.getState().auth.loggedIn
    if(continueFlow === flowName) {
        const box = store.getState().site.box
        let newSteps
        if(loggedIn) {
            newSteps = [stepOptions.SHOP, stepOptions.PAYMENT]
        } else {
            newSteps = box?.digital_form_id ? [stepOptions.SHOP, stepOptions.PERSONAL_DETAILS, stepOptions.WAIVER, stepOptions.PAYMENT] : [stepOptions.SHOP, stepOptions.PERSONAL_DETAILS, stepOptions.PAYMENT]
        }
        return { steps: newSteps }
    } else {
        return {
            flowName: flowName,
            steps: loggedIn ? [stepOptions.SHOP, stepOptions.PAYMENT] : [stepOptions.SHOP],
            currStep: stepOptions.SHOP,
            flags: {}
        }
    }
}

//</editor-fold>

//<editor-fold desc="save flow">

export const saveFlow = async (values, dispatch) => {
    try {
        console.log('saveFlow', values)
        const relevantStore = getRelevantStore()
        const flowName = relevantStore.stepper?.flowName
        switch (flowName) {
            case flowOptions.REGISTER:
                await registerUser(values, dispatch)
                break;
            case flowOptions.MEMBERSHIP_PURCHASE:
            case flowOptions.DIRECT_MEMBERSHIP_PURCHASE:
                await membershipPurchase(values, dispatch);
                break;
            case flowOptions.GROUP_SESSION:
            case flowOptions.DIRECT_GROUP_SESSION:
            case flowOptions.STAFF_APPOINTMENT:
            case flowOptions.SPACE_APPOINTMENT:
            case flowOptions.DIRECT_STAFF_APPOINTMENT:
            case flowOptions.DIRECT_SPACE_APPOINTMENT:
                await bookUserToSession(values, dispatch)
                break;
            case flowOptions.WAIVER:
                await signBoxWaiver(values, dispatch)
                break;
            case flowOptions.COURSE:
            case flowOptions.DIRECT_COURSE:
                await bookToCourse(values, dispatch)
                break;
            case flowOptions.PRODUCT_PURCHASE:
            case flowOptions.DIRECT_PRODUCT_PURCHASE:
                await purchaseProduct(values, dispatch)
                break;
            case flowOptions.DIRECT_TRIAL_GROUP:
                await bookTrialClass(values, dispatch)
                break;
            case flowOptions.LEAD_FORM:
                await addNewLead(values, dispatch)
                break;
            case flowOptions.KIOSK_CLOSE_DEBT:
            case flowOptions.KIOSK_CLOSE_ALL_DEBTS:
                await kioskCloseDebt(values, dispatch)
                break;
            default:
                break;
        }
    } catch(error) {
        console.log('hererererer', error)
        handleRecordSessionOnFlowError()
        dispatch(cleanStepper())
    }
}

const purchaseProduct = async (values, dispatch) => {
    const loggedIn = store.getState().auth.loggedIn
    if(loggedIn) {
        await membershipPurchase(values, dispatch)
    } else {
        await registerUser({...values, isProduct: true}, dispatch)
    }
}

const membershipPurchase = async (values, dispatch) => {
    try {
        dispatch(setLoadingType(LOADING_PAYMENT))
        const box = store.getState().site.box
        dispatch(updateFlowRes(values.res))
        dispatch(fetchUserMembershipsByBox({boxId: box.id, active: true}))
        handlePurchasePixel(values.payment_info?.cart)
        dispatch(setLoadingType(null))
    } catch (error) {
        dispatch(updateError(error.error?.messageToUser ?? t('payment-error-screen-message')))
        dispatch(setLoadingType(null))
        throw error
    }
}

const bookToCourse = async (values, dispatch) => {
    try {
        dispatch(setLoadingType(LOADING_PAYMENT))
        const {route} = getInfoForCourseBooking(values)
        const res = values.res
        if(route === 'payments/purchase') {
            dispatch(updateFlowRes(res))
        } else if(route === 'registerUser') {
            // res=true when the parent is already logged in, so in any other case it's a new user and we'll want to log him in
            if((typeof res === 'object') && res?.token) dispatch(addTokenAfterRegister(res))
            dispatch(updateFlowRes(res ?? true))
        }
        handlePurchasePixel(values.payment_info?.cart)
        dispatch(setLoadingType(null))
    } catch (error) {
        console.log('error', error)
        dispatch(updateError(error.error?.messageToUser ?? t('payment-error-screen-message')))
        dispatch(setLoadingType(null))
        throw error
    }
}

const getInfoForCourseBooking = (values) => {
    const loggedIn = store.getState().auth.loggedIn
    const flags = store.getState().stepper.flags
    const userBox = store.getState().user.activeUsersBoxes
    let params = {}
    let route = loggedIn ? 'payments/purchase' : 'registerUser'
    let course = flags.courseInfo;

    const cart = values.payment_info?.cart?.reduce((acc, item) => {
        if(course?.id === item.series_id) return [...acc, {...item, start: item.start ?? course.start_date, end: course.end_date, series_id: course.id, register_group_member: flags.isChildRegistration, selectedSeriesEvents: values.selectedSeriesEvents ?? null}]
        return [...acc, item]
    },[])
    params = {
        course,
        boxes_id: course.box_fk,
        cart: cart,
        payment_info: values.payment_info,
        isChildRegistration: flags.isChildRegistration,
        selectedSeriesEvents: values.selectedSeriesEvents ?? null
    }
    if(flags.isChildRegistration) {
        if(loggedIn) {
            if(values.child.child_id) {
                params.group_info = values.child
                route = 'payments/purchase'
            } else {
                params.parent = { id: userBox.user_fk}
                params.child = values.child
                route = 'registerUser'
            }
        } else {
            params.child = values.child
            params.parent = {
                first_name: values.first_name,
                last_name: values.last_name,
                email: values.email,
                phone: values.phone,
                birthday: values.birthday,
                allow_mailing_list: values.allow_mailing_list,
                allow_sms: values.allow_sms,
                lead_id: values.lead_id,
                customFields: values.customFields,
                gender: values.gender
            }
            route = 'registerUser'
        }
        params.locations_box_fk = values.locations_box_fk ?? values.course?.locations_box_fk
        params.digitalFormInfo = values.digitalFormInfo ?? null
    } else {
        params = {...params, ...values}
    }
    return {params, route};
}

const registerUser = async (values, dispatch) => {
    try {
        dispatch(setLoadingType(LOADING_PAYMENT))
        let res = values.res
        const flowRes = values.payment_info?.cart?.map(item => ({membership_types: item}))
        //ONLY in register without membership we will execute this flow manually!!!!! (otherwise - the payment process will do that)
        if(values.continueWithoutMembership && !values.res && !values.payment_info) {
            res = await registerWithoutMembership(values)
        }
        dispatch(updateFlowRes({...res, memberships: flowRes}))
        if(!values.isProduct) dispatch(addTokenAfterRegister(res))
        dispatch(setLoadingType(null))
        if(values.continueWithoutMembership) {
            sendPixelEvent(pixelEvents.COMPLETE_REGISTRATION_NO_MEMBERSHIP)
        } else {
            handlePurchasePixel(values.payment_info?.cart)
        }
    } catch (error) {
        console.log('error', error)
        dispatch(updateError(error.error?.messageToUser ?? t('payment-error-screen-message')))
        callSiteLog(paymentErrorCatchLog, {error}, paymentLogGroup)
        dispatch(setLoadingType(null))
        throw error
    }
}

const registerWithoutMembership = async (values) => {
    const box = store.getState().site.box
    const params = {
        ...values,
        boxes_id: box.id,
        cart: null
    }
    return await apiAction(`registerUser`, 'post', params);
}

const getRegisterUserRequestParams = (values) => {
    const box = store.getState().site.box
    //probably always null after server move
    const cart = values.payment_info?.cart?.reduce((acc, item) => {
        const cartItem = values.cart?.find(i => i.id === item.id)
        if(cartItem) return [...acc, {...cartItem, ...item}]
        return [...acc, item]
    },[])
    const params = {
        ...values,
        boxes_id: box.id,
        cart: values.continueWithoutMembership ? null : cart
    }
    return params
}

const addNewLead = async (values, dispatch) => {
    try {
        dispatch(setLoadingType(LOADING_PAGE))
        const box = store.getState().site.box
        const res = await apiAction(`createLead`, 'post', {...values, boxes_id: box.id});
        dispatch(updateFlowRes(res))
        dispatch(setLoadingType(null))
    } catch (error) {
        console.log('error', error)
        dispatch(updateError(error.error?.messageToUser))
        dispatch(setLoadingType(null))
        throw error
    }

}

const bookUserToSession = async (values, dispatch) => {
    try {
        const loggedIn = store.getState().auth.loggedIn
        dispatch(setLoadingType(LOADING_PAYMENT))
        if (loggedIn) {
            await handleExisingUserActions(values, dispatch)
        } else {
            await handleBookAndRegisterNewUser(values,dispatch)
        }
        dispatch(setLoadingType(null))
    } catch(error) {
        dispatch(setLoadingType(null))
        console.log('bookUserToSession error', error)
        dispatch(updateError(error.error?.messageToUser ?? t('booking-error')))
        throw error
    }
}

const bookTrialClass = async (values, dispatch) => {
    try {
        dispatch(setLoadingType(LOADING_PAYMENT))
        await handleBookAndRegisterNewUser(values,dispatch, true)
        dispatch(setLoadingType(null))
    } catch(error) {
        dispatch(setLoadingType(null))
        console.log('bookTrialClass error', error)
        dispatch(updateError(error.error?.messageToUser ?? t('trial-booking-error')))
        throw error
    }
}

const signBoxWaiver = async (values, dispatch) => {
    try {
        dispatch(setLoadingType(LOADING_PAYMENT))
        const res = await apiAction('digitalForm', 'post', values.digitalFormInfo);
        dispatch(updateFlowRes(res))
        dispatch(setLoadingType(null))
    } catch(error) {
        dispatch(setLoadingType(null))
        console.log('sign waiver error', error)
        dispatch(updateError(error.error.messageToUser))
        throw error
    }
}

const kioskCloseDebt = async (values, dispatch) => {
    try {
        dispatch(setKioskLoadingType(LOADING_PAYMENT))
        // const {route, method} = getFlowEndpoint()
        // const res = await apiAction(route, method, values);
        dispatch(updateFlowRes(values.res))
        dispatch(setKioskLoadingType(null))
    } catch(error) {
        dispatch(setKioskLoadingType(null))
        dispatch(updateError(error.error?.messageToUser ?? t('debt-close-error')))
        throw error
    }
}

export const getSuccessProp = ({navigate}) => {
    const relevantStore = getRelevantStore()
    const project_type = relevantStore.project?.project_type;
    const flowName = relevantStore.stepper?.flowName
    const flowRes = relevantStore.stepper?.flowRes
    const type = project_type === projectTypes.site ? getRegisterCtaInfo() : null;
    const language = relevantStore.site?.language
    const schedule = flowRes?.schedule_info ?? flowRes
    console.log('getSuccessProp flowRes',flowRes, relevantStore.stepper)
    const route = type === 'appointment' ? 'staff-appointment' : type; //fix wrong route on appointment without causing too many changes

    switch (flowName) {
        case flowOptions.REGISTER:
            return {
                header: flowRes?.memberships ? t('register-success-header') : t('register-wo-membership-success-header'),
                subheader: flowRes?.memberships ? t('register-success-subheader') : t('register-wo-membership-success-subheader'),
                ctaText: t(`success-cta-${type}`),
                svgType: 'register',
                onCTAClick: route ? () => onLinkClicked(`/${route}`, navigate) : null
            };
        case flowOptions.MEMBERSHIP_PURCHASE:
        case flowOptions.DIRECT_MEMBERSHIP_PURCHASE:
        case flowOptions.PRODUCT_PURCHASE:
        case flowOptions.DIRECT_PRODUCT_PURCHASE:
            const isProduct = flowName === flowOptions.PRODUCT_PURCHASE || flowName === flowOptions.DIRECT_PRODUCT_PURCHASE
            const itemName = flowRes?.memberships ? flowRes.memberships[0]?.membership_types?.name : '' //todo fix for multi items in cart
            const isCart = flowRes?.memberships && flowRes?.memberships.length > 1
            return {
                header: t(`${isProduct ? 'product' : 'membership'}-success-header`),
                subheader: t(`${isProduct ? 'product' : 'membership'}-success-subheader${isCart ? '-cart' : ''}`, {name: itemName, interpolation: {'escapeValue': false}}),
                ctaText: t(`success-cta-${type}`),
                svgType: isProduct ? 'shop' : 'membership',
                onCTAClick: route ? () => window.open(`/${route}`) : null
            };
        case flowOptions.GROUP_SESSION:
        case flowOptions.DIRECT_GROUP_SESSION:
            return {
                header: t('group-session-success-header'),
                subheader: t('group-session-success-subheader', {
                    date: dayjs(schedule.date, DB_DATE_FORMAT).locale(language?.code ?? 'en').format(`dddd, ${language?.code === 'he' ? 'D בMMMM' : 'MMMM D'}, YYYY`),
                    time: dayjs(schedule.time, 'HH:mm').format(schedule.locations_box.time_format ?? 'HH:mm'),
                    sessionName: schedule?.box_categories?.name ?? '',
                    interpolation: {'escapeValue': false}
                }),
                svgType: 'schedule',
                onCTAClick: null
            }
        case flowOptions.DIRECT_TRIAL_GROUP:
            return {
                header: t('trial-class-success-header'),
                subheader: t(`trial-class-success-subheader`, {
                    date: dayjs(schedule.date, DB_DATE_FORMAT).locale(language?.code ?? 'en').format(`dddd, ${language?.code === 'he' ? 'D בMMMM' : 'MMMM D'}, YYYY`),
                    time: dayjs(schedule.time, 'HH:mm').format(schedule.locations_box.time_format ?? 'HH:mm'),
                    sessionName: schedule?.box_categories?.name ?? '',
                    interpolation: {'escapeValue': false}
                }),
                svgType: 'schedule',
                onCTAClick: null,
                hideApp: true
            }
        case flowOptions.WAIVER:
            return {
                header: t('waiver-success-header'),
                subheader: t('waiver-success-subheader'),
                ctaText: t(`success-cta-${type}`),
                svgType: 'waiver',
                onCTAClick: route ? () => window.open(`/${route}`) : null,
                hideApp: true
            }
        case flowOptions.STAFF_APPOINTMENT:
        case flowOptions.SPACE_APPOINTMENT:
        case flowOptions.DIRECT_STAFF_APPOINTMENT:
        case flowOptions.DIRECT_SPACE_APPOINTMENT:
            const flow = flowName.replace('DIRECT_', '')
            return {
                header: t(`appointment-success-header`),
                subheader: t(`${flow}-success-subheader`, {
                    date: dayjs(schedule.date, DB_DATE_FORMAT).locale(language?.code ?? 'en').format(`dddd, ${language?.code === 'he' ? 'D בMMMM' : 'MMMM D'}, YYYY`),
                    time: dayjs(schedule.time, 'HH:mm').format(schedule.locations_box.time_format ?? 'HH:mm'),
                    sessionName: schedule?.box_categories?.name ?? '',
                    staffOrSpace: (flowName === flowOptions.STAFF_APPOINTMENT || flowName === flowOptions.DIRECT_STAFF_APPOINTMENT) ? schedule?.coach?.full_name : schedule?.spaces?.name,
                    interpolation: {'escapeValue': false}
                }),
                svgType: 'schedule',
                onCTAClick: null
            }
        case flowOptions.COURSE:
        case flowOptions.DIRECT_COURSE:
            const flags = store.getState().stepper.flags
            const location = store.getState().site.selectedLocation
            const course = flags.courseInfo
            let days = sortCourseEventDays(course.series_events).map(day => {
                return t('event-day-time', {day: t(`day-${day.day_of_week}`), time: dayjs(day.start, DB_TIME_FORMAT).format(location.time_format)})
            })
            const endDateStr = course.end_date ? ` - ${dayjs(course.end_date, DB_DATE_FORMAT).format(location.date_format)}` : '';
            const startDate = (course.allow_mid_booking && course.has_started) ? dayjs().format(location.date_format) : dayjs(course.start_date, DB_DATE_FORMAT).format(location.date_format)
            return {
                header: t(`course-success-header`),
                subheader: t(`course-success-subheader`, {
                    dateRange: `${startDate}${endDateStr}`,
                    days: days.join(', '),
                    courseName: course?.series_name ?? '',
                    interpolation: {'escapeValue': false}
                }),
                svgType: 'schedule',
                onCTAClick: null,
                hideApp: true
            }
        case flowOptions.LEAD_FORM:
            return {
                header: t('form-lead-header-success'),
                subheader: t('form-lead-subheader-success'),
                newSuccessFormat: true,
                hideApp: true
            }
        case flowOptions.KIOSK_CLOSE_DEBT:
        case flowOptions.KIOSK_CLOSE_ALL_DEBTS:
            const name = flowRes?.map(i => `\"${i.membership_type_name}\"`)?.join('\n')
            const subheader = flowName === flowOptions.KIOSK_CLOSE_ALL_DEBTS ? `${t('kiosk-close-all-debt-subheader-success')} \n${name}` : t('kiosk-close-debt-subheader-success', {name, interpolation: {'escapeValue': false}})
            return {
                header: t('kiosk-close-debt-header-success'),
                subheader: subheader,
                hideApp: true,
                svgType: 'shop'
            }
        default:
            return {};
    }
}

const getRegisterCtaInfo = () => {
    let type = null
    const pages = getBoxPageOptions()
    if(pages.hasClasses) {
        type = 'group'
    }
    else if(pages.hasAvailabilities) {
        type = 'appointment'
    }
    else if(pages.hasCourses) {
        type = 'course'
    }
    else if(pages.hasProducts) {
        type = 'shop'
    }
    return type;
}

const convertValuesToServerInsertParams = (values) => {
    const box = store.getState().site.box
    const flowName = store.getState().stepper.flowName
    const isAvailability = [flowOptions.STAFF_APPOINTMENT, flowOptions.SPACE_APPOINTMENT, flowOptions.DIRECT_STAFF_APPOINTMENT, flowOptions.DIRECT_SPACE_APPOINTMENT].includes(flowName);
    let extras = null

    if(values.payForSlot) {
        extras = {
            payForSlot: true,
            purchaseData: {
                cart: values.payment_info?.cart, //probably always null after server move
                payment_object: values.payment_info,
                boxes_id: box.id
            },
            digitalFormInfo: values.digitalFormInfo ?? null // happens (currently) only when user is logged in and booking group session
        }
    }

    if(isAvailability) {
        extras = extras ? {...extras, ...getAvailabilityExtras(values.schedule)} : getAvailabilityExtras(values.schedule)
    }

    return {
        extras: extras,
        membership_user_id: values.id,
        schedule_id: values.schedule.id,
        uuidPayment: values.uuidPayment
    }
}

const handleExisingUserActions = async (values, dispatch) => {
    const params = convertValuesToServerInsertParams(values)
    const flowName = store.getState().stepper.flowName
    const isAvailability = [flowOptions.STAFF_APPOINTMENT, flowOptions.SPACE_APPOINTMENT, flowOptions.DIRECT_STAFF_APPOINTMENT, flowOptions.DIRECT_SPACE_APPOINTMENT].includes(flowName);
    let res = {};
    switch (values.schedule.booking_option) {
        case bookingTypes.INSERT_SCHEDULE_USER:
            if(!values.payForSlot && !values.res) {
                res = await apiAction('scheduleUser/insert', 'post', params);
            } else {
                res = values.res
            }
            res = values.payForSlot || isAvailability ? res.data : {...res.data, showSuccessToast: {title: t('success-booking-header'), content: t('success-booking-content')}}
            if(values.payForSlot) {
                handlePurchasePixel(values.payment_info.cart)
            }
            break;
        case bookingTypes.INSERT_STAND_BY:
            res = await apiAction('scheduleStandBy/insert', 'post', params);
            res = {...res.data, showSuccessToast: {title: t('success-standby-header'), content: t('success-standby-content')}}
            break;
        case bookingTypes.CANCEL_WAIT_LIST:
            res = await apiAction('scheduleStandBy/delete', 'post', {schedule_stand_by_id: values.schedule.user_in_standby});
            res = {...res.data, showSuccessToast: {title: t('success-cancel-standby-header'), content: t('success-cancel-standby-content')}}
            break;
        case bookingTypes.CANCEL_SCHEDULE_USER:
            let lateCancellation = false
            try {
                await apiAction(`scheduleUser/checkLateCancel`, 'post', {schedule_id: values.schedule.id});
            } catch (error) {
                lateCancellation = error.error.code === 513
            }
            res = await apiAction('scheduleUser/delete', 'post', {schedule_user_id: values.schedule.user_booked, schedule_id: values.schedule.id, late_cancel: lateCancellation});
            res = {...res.data, showSuccessToast: {title: t('success-cancel-booking-header'), content: t('success-cancel-booking-content')}}
            break;
        default:
            break;
    }
    dispatch(updateFlowRes(res))
}

const handleBookAndRegisterNewUser = async (values, dispatch, isTrial = false) => {
    dispatch(updateFlowRes(values.res))
    if(!isTrial) dispatch(addTokenAfterRegister(values.res))
    handlePurchasePixel(values.payment_info?.params?.extras?.purchaseData?.cart)
}

const getBookAndRegisterUserRequestParams = (values, isTrial) => {
    const box = store.getState().site.box
    const flowName = store.getState().stepper.flowName
    const isAvailability = [flowOptions.STAFF_APPOINTMENT, flowOptions.SPACE_APPOINTMENT, flowOptions.DIRECT_STAFF_APPOINTMENT, flowOptions.DIRECT_SPACE_APPOINTMENT].includes(flowName);
    let extras = {
        payForSlot: true,
        purchaseData: {
            cart: values.payment_info?.cart, //probably always null after server move
            payment_object: values.payment_info,
            boxes_id: box.id
        }
    }

    if(isAvailability) {
        extras = {...extras, ...getAvailabilityExtras(values.schedule)}
    }

    const params = {
        ...values,
        isTrial: isTrial,
        boxes_id: box.id,
        extras: extras
    }
    //remove double props (it's inside extras)
    delete params.payment_info
    delete params.payForSlot
    delete params.id
    delete params.cart

    return params
}

const getAvailabilityExtras = (slot) => {
    return {
        availability: true,
        id: slot.id,
        box_categories_id: slot.box_categories.id,
        box_fk: slot.box_fk,
        date: slot.date,
        time: slot.time,
        end_time: slot.end_time,
        end_time_with_break: slot.end_time_with_break,
        coach_ub_id: slot.coach?.id, // Just for validations, will get data from DB
        spaces_id: slot.space?.id
    }
}

export const handlePurchasePixel = (cart, event = null) => {
    try {
        if(cart) {
            const info = cart.reduce((acc, item) => {
                acc.totalPrice += item.price
                acc.contents = [...acc.contents, { id: item.name, quantity: item.quantity ?? 1, item_price: item.price }]
                acc.items = [...acc.items, { item_name: item.name, quantity: item.quantity ?? 1, price: item.price }] //for gtm
                acc.ids = [...acc.ids, JSON.parse(JSON.stringify(item.name))] //ids are the membership types names (the actual mt id means nothing to the box). also we do the parsing because otherwise hebrew chars are replaced for their coded values. this for some reason doesn't work in "contents".
                return acc
            }, {contents: [], totalPrice: 0, ids: [], items: []})
            const params = {
                value: info.totalPrice, //this is the original pricing of each membership and not the actual price they paid! (aka no promo discounts etc.)
                content_ids: info.ids,
                num_items: info.ids.length,
                contents: info.contents,
                items: info.items,
                transaction_id: uuid()
            }
            console.log('handlePurchasePixel params', params)
            sendPixelEvent(event ?? pixelEvents.PURCHASE, params)
        }
    } catch (error) {
        console.log('error in getMembershipsDataForPixel', error)
    }
}

//</editor-fold>

//<editor-fold desc="export helpers">

export const showStepsProgress = {
    REGISTER: true,
    MEMBERSHIP_PURCHASE: false,
    DIRECT_MEMBERSHIP_PURCHASE: false,
    WAIVER: false
}

export const getDirectItemCta = (dispatch, params, values) => {
    const flowName = store.getState().stepper.flowName
    const userBox = store.getState().user.activeUsersBoxes
    switch (flowName) {
        case flowOptions.DIRECT_MEMBERSHIP_PURCHASE:
            const urlSearchParams = new URLSearchParams(window.location.search);
            const urlParams = Object.fromEntries(urlSearchParams.entries());
            const globalLocation = store.getState().site.selectedLocation
            const loggedIn = store.getState().auth.loggedIn
            const cartFirstItem = values.cart && values.cart.length ? values.cart[0] : null
            console.log('in1', globalLocation?.id, cartFirstItem, urlParams)
            if(urlParams.overrideLocation === 'true' && !loggedIn && cartFirstItem && cartFirstItem?.payment_locations && !cartFirstItem.payment_locations?.includes(globalLocation?.id)) {
                console.log('in2', globalLocation.id, values)
                return {text: t('membership-wrong-location-hp')}
            }

            return {text: t('specific-page-cta'), onClick: () => {
                    if(cartFirstItem.type === membershipTypes.PUNCH_CARD && cartFirstItem.allow_quantity) {
                        dispatch(openModal({modalName: SELECT_QUANTITY_MODAL}))
                    } else {
                        handleMembershipStartDate(cartFirstItem, dispatch)
                    }
                }
            }
        case flowOptions.DIRECT_GROUP_SESSION:
            return {text: getBookingCtaText(params.action), onClick: params.action === bookingTypes.PAST ? null : params.onCtaClick}
        case flowOptions.DIRECT_COURSE:
            const course = params.course
            if(!course) return {}
            let text = 'specific-page-cta'
            if(course.isHugFull) text = 'full-course'
            if(course.past && !course.allow_mid_booking) text = 'past-course'
            if(course.end_date && dayjs(course.end_date, DB_DATE_FORMAT).diff(dayjs(), 'days') < 0) text = 'course-over'
            if(course.isBookedUser) text = 'user-booked-to-course'
            return {
                text: t(text),
                onClick: text !== 'specific-page-cta' ? null : (courseBookingAllowed(userBox, course) ? params.onCtaClick : null)
            }
        default:
            return {text: t('specific-page-cta'), onClick: () => null}
    }
}

export const getDirectItemDescriptionComponent = (values) => {
    const flowName = store.getState().stepper.flowName
    switch (flowName) {
        case flowOptions.DIRECT_MEMBERSHIP_PURCHASE:
            const cartFirstItem = values.cart && values.cart.length ? values.cart[0] : null
            return <MembershipDescription membership={cartFirstItem}/>
        case flowOptions.DIRECT_GROUP_SESSION:
            return <GroupSessionDescription session={values.schedule}/>
        case flowOptions.DIRECT_COURSE:
            return <CourseDescription course={values.course}/>
        default:
            return null
    }
}

export const getDirectItemAdditionalInformationComponent = (values) => {
    const flowName = store.getState().stepper.flowName
    switch (flowName) {
        case flowOptions.DIRECT_MEMBERSHIP_PURCHASE:
            const cartFirstItem = values.cart && values.cart.length ? values.cart[0] : null
            return <MembershipInfo membership={cartFirstItem}/>
        case flowOptions.DIRECT_GROUP_SESSION:
            return <GroupSessionInfo session={values.schedule}/>
        case flowOptions.DIRECT_COURSE:
            return <CourseInfo course={values.course}/>
        default:
            return null
    }
}
export const getDirectItemOnQuantityRes = () => {
    const flowName = store.getState().stepper.flowName
    switch (flowName) {
        case flowOptions.DIRECT_MEMBERSHIP_PURCHASE:
            return (quantity, values, setFieldValue, dispatch) => {
                const cartFirstItem = values.cart && values.cart.length ? values.cart[0] : {}
                setFieldValue('cart', [{...cartFirstItem, quantity: quantity}])
                dispatch(updateModalRes({modalName: SELECT_QUANTITY_MODAL, res: null}))
                setTimeout(() => handleMembershipStartDate(cartFirstItem, dispatch), 100)
            }
        case flowOptions.DIRECT_GROUP_SESSION:
        case flowOptions.DIRECT_COURSE:
            return null;
        default:
            return null
    }
}
//</editor-fold>

//<editor-fold desc="flow and values updating logics (before and after sign in mostly)">
export const checkShouldResetValues = (currFlow, newFlow) => {
    const flags = store.getState().stepper.flags //check fromMembershipSignIn just in case, this is just an extra check to be 100% sure, but it's not a must
    let defaultCheck = !!(overrideValues(newFlow) || (currFlow && currFlow !== newFlow))
    if(newFlow === flowOptions.REGISTER && [flowOptions.MEMBERSHIP_PURCHASE, flowOptions.DIRECT_MEMBERSHIP_PURCHASE].includes(currFlow) && flags.fromMembershipSignIn) {
        defaultCheck = false;
    }
    return defaultCheck
}

export const overrideValues = (flowName) => {
    switch (flowName) {
        case flowOptions.GROUP_SESSION:
        case flowOptions.DIRECT_GROUP_SESSION:
        case flowOptions.STAFF_APPOINTMENT:
        case flowOptions.SPACE_APPOINTMENT:
        case flowOptions.DIRECT_STAFF_APPOINTMENT:
        case flowOptions.DIRECT_SPACE_APPOINTMENT:
        case flowOptions.COURSE:
        case flowOptions.DIRECT_COURSE:
        case flowOptions.PRODUCT_PURCHASE:
        case flowOptions.DIRECT_PRODUCT_PURCHASE:
        case flowOptions.MEMBERSHIP_PURCHASE:
        case flowOptions.DIRECT_MEMBERSHIP_PURCHASE:
        case flowOptions.REGISTER:
            return false
        default:
            return true
    }
}

export const shouldOpenSignIn = ({flowName, currStep, values}) => {
    switch (flowName) {
        case flowOptions.PRODUCT_PURCHASE:
        case flowOptions.DIRECT_PRODUCT_PURCHASE:
            return currStep === stepOptions.SHOP
        case flowOptions.GROUP_SESSION:
        case flowOptions.DIRECT_GROUP_SESSION:
            return !!values.payForSlot && currStep === stepOptions.GROUP_SESSIONS
        case flowOptions.STAFF_APPOINTMENT:
        case flowOptions.SPACE_APPOINTMENT:
        case flowOptions.DIRECT_STAFF_APPOINTMENT:
        case flowOptions.DIRECT_SPACE_APPOINTMENT:
            return !!values.payForSlot && currStep === stepOptions.AVAILABILITY_SLOT
        case flowOptions.COURSE:
        case flowOptions.DIRECT_COURSE:
            return currStep === stepOptions.COURSES
        case flowOptions.MEMBERSHIP_PURCHASE:
            return currStep === stepOptions.SELECT_MEMBERSHIP
        case flowOptions.DIRECT_MEMBERSHIP_PURCHASE:
            return currStep === stepOptions.DIRECT_ITEM
        default:
            return false
    }
}

//the array we return from this function in the keys we want to keep their values even after a new sign in has been made (different or the same as the previous sign in)
//for example if user in shop entered a new mail in box and then went back to the shop step, and filled the sign in again with an existing user's mail, then the steps will change, but we also need to reset all the previous values (from personal details, forms etc.) , except 'cart' and 'locations_box_fk'
export const getFlowPersistedProps = (flowName) => {
    switch (flowName) {
        case flowOptions.PRODUCT_PURCHASE:
        case flowOptions.DIRECT_PRODUCT_PURCHASE:
            return ['cart', 'locations_box_fk']
        case flowOptions.GROUP_SESSION:
        case flowOptions.DIRECT_GROUP_SESSION:
            return ['action', 'id', 'locations_box_fk', 'payForSlot', 'schedule', 'trial']
        case flowOptions.STAFF_APPOINTMENT:
        case flowOptions.SPACE_APPOINTMENT:
        case flowOptions.DIRECT_STAFF_APPOINTMENT:
        case flowOptions.DIRECT_SPACE_APPOINTMENT:
            return ['id', 'locations_box_fk', 'payForSlot', 'schedule', 'service']
        case flowOptions.COURSE:
        case flowOptions.DIRECT_COURSE:
            return ['course', 'serviceId', 'locations_box_fk', 'selectedSeriesEvents', 'trialSessionId']
        case flowOptions.MEMBERSHIP_PURCHASE:
            return ['cart']
        case flowOptions.DIRECT_MEMBERSHIP_PURCHASE:
            return ['cart', 'locations_box_fk']
        default:
            return null
    }
}

export const handleAsyncChecksAfterSignIn = async (flowName, props) => {
    switch (flowName) {
        case flowOptions.GROUP_SESSION:
        case flowOptions.DIRECT_GROUP_SESSION:
            return await handleGroupAfterSignInRes(props)
        case flowOptions.COURSE:
        case flowOptions.DIRECT_COURSE:
            return await handleHugAfterSignInRes({flowName, ...props})
        case flowOptions.MEMBERSHIP_PURCHASE:
        case flowOptions.DIRECT_MEMBERSHIP_PURCHASE:
            return handleMembershipAfterSignIn(props)
        default:
            return true
    }
}

const handleGroupAfterSignInRes = async ({values, navigate, dispatch}) => {
    try {
        const loggedIn = store.getState().auth.loggedIn
        const userBox = store.getState().user.activeUsersBoxes
        if(loggedIn) {
            if(values.trial) {
                onLinkClicked(`/group`, navigate)
                window.location.reload()
                return false
            } else {
                return await checkClassBeforePayment(values)
                //check because user might already be registered to session
            }
        } else {
            if(values.trial) {
                onLinkClicked(`/group/trial/${values.schedule?.id}`, navigate)
                return false
            }
        }
        return true
    } catch(error) {
        console.log('handleGroupAfterSignInRes error', error)
        if(error?.error?.messageToUser) dispatch(updateError(error.error.messageToUser))
        return false
    }
}

export const checkClassBeforePayment = async (values) => {
    const loggedIn = store.getState().auth.loggedIn
    const userBox = store.getState().user?.activeUsersBoxes
    if(loggedIn && !values?.schedule?.availability) {
        //check because user might already be registered to session
        const isRegistered = await apiAction('scheduleUser/checkIfRegistered', 'post', {users_id: userBox?.user_fk, schedule_id: values?.schedule?.id});
        if(isRegistered) {
            window.location.reload()
            return false
        }
    }
    if(values.payForSlot) {
        await apiAction('checkUserPayForSlotLimitations', 'post', {ub_id: userBox?.ub_id, schedule_id: values?.schedule?.availability ? null : values?.schedule?.id, membership_type_id: values?.id});
    }
    return true
}

export const onNextStep = async ({flowName, currStep, values, dispatch}) => {
    const loggedIn = store.getState().auth.loggedIn
    switch(flowName) {
        case flowOptions.GROUP_SESSION:
        case flowOptions.DIRECT_TRIAL_GROUP:
        case flowOptions.DIRECT_GROUP_SESSION:
            if(!loggedIn && [stepOptions.PERSONAL_DETAILS, stepOptions.TRIAL_GROUP_DETAILS].includes(currStep) && !!values.payForSlot) {
                try {
                    await apiAction('checkUserPayForSlotLimitations', 'post', {ub_id: null, schedule_id: values?.schedule?.id, membership_type_id: values?.id, new_user_props: {gender: values.gender, birthday: values.birthday}});
                } catch(error) {
                    console.log('onNextStep error', error)
                    if(error?.error?.messageToUser) dispatch(updateError(error.error.messageToUser))
                    return false
                }
            }
            break;
    }
    return true
}

const handleHugAfterSignInRes = async ({flowName, values, navigate}) => {
    try {
        const loggedIn = store.getState().auth.loggedIn
        const userBox = store.getState().user.activeUsersBoxes
        if(loggedIn) {
            if(values.trialSessionId) {
                window.location.reload()
                return false
            } else {
                const isRegistered = await apiAction('hugim/checkIfUserAlreadyBookedToHug', 'post', {
                    users_id: userBox.user_fk,
                    series_id: values.course.id
                });
                if (isRegistered) {
                    window.location.reload()
                    return false
                }
            }
        } else {
            if(values.trialSessionId) {
                onLinkClicked(`/group/trial/${values.trialSessionId}`, navigate)
                if(flowName === flowOptions.DIRECT_COURSE) window.location.reload() //if this is from specific course page, navigate and then reload page
                return false
            }
        }
        return true
    } catch(error) {
        console.log('handleHugAfterSignInRes error', error)
        return false
    }
}

const handleMembershipAfterSignIn = ({values, navigate, dispatch, setFieldValue}) => {
    const loggedIn = store.getState().auth.loggedIn
    if(!values.cart?.length) { //just in case, I don't know
        window.location.reload()
        return false
    }
    if(!loggedIn) {
        setFieldValue('cart', null)
        dispatch(updateFlags({fromMembershipSignIn: true}))
        onLinkClicked('/register', navigate, {state: {membership: values.cart}})
        return false
    } else {
        const hasNewUserMt = values?.cart?.some(item => item.new_users_membership_prop_exists)
        if(hasNewUserMt) {
            onLinkClicked('/404', navigate, {state: {notFoundHeader: t('new-user-membership-logged-in-error-header'), notFoundSubHeader: t('new-user-membership-logged-in-error-subheader')}})
            return false
        }
    }
    return true
}

export const loginAdditionalProps = ({flowName, values}) => {
    switch(flowName) {
        case flowOptions.DIRECT_MEMBERSHIP_PURCHASE:
            return values?.cart?.length ? {membership: values.cart[0]} : {}
        default:
            return {}
    }
}

export const getRequestReadyValues = (values) => {
    const relevantStore = getRelevantStore()
    const flowName = relevantStore.stepper?.flowName
    const loggedIn = relevantStore.auth?.loggedIn

    switch (flowName) {
        case flowOptions.REGISTER:
            return getRegisterUserRequestParams(values)
        case flowOptions.MEMBERSHIP_PURCHASE:
        case flowOptions.DIRECT_MEMBERSHIP_PURCHASE:
            return getRegisterUserRequestParams(values) //it's pretty mush the same, i didn't wanted to duplicate
        case flowOptions.GROUP_SESSION:
        case flowOptions.DIRECT_GROUP_SESSION:
        case flowOptions.STAFF_APPOINTMENT:
        case flowOptions.SPACE_APPOINTMENT:
        case flowOptions.DIRECT_STAFF_APPOINTMENT:
        case flowOptions.DIRECT_SPACE_APPOINTMENT:
            if (loggedIn) {
                return convertValuesToServerInsertParams(values)
            } else {
                return getBookAndRegisterUserRequestParams(values, false)
            }
        case flowOptions.COURSE:
        case flowOptions.DIRECT_COURSE:
            const {params} = getInfoForCourseBooking(values)
            return params
        case flowOptions.PRODUCT_PURCHASE:
        case flowOptions.DIRECT_PRODUCT_PURCHASE:
            return getRegisterUserRequestParams({...values, isProduct: true})
        case flowOptions.DIRECT_TRIAL_GROUP:
            return getBookAndRegisterUserRequestParams(values, true)
        case flowOptions.KIOSK_CLOSE_DEBT:
        case flowOptions.KIOSK_CLOSE_ALL_DEBTS:
            return values
        default:
            return null;
    }
}

export const getFlowEndpoint = (values) => {
    const relevantStore = getRelevantStore()
    const flowName = relevantStore.stepper?.flowName
    const loggedIn = relevantStore.auth?.loggedIn

    switch (flowName) {
        case flowOptions.REGISTER:
            return {route: 'registerUser', method: 'post'}
        case flowOptions.MEMBERSHIP_PURCHASE:
        case flowOptions.DIRECT_MEMBERSHIP_PURCHASE:
            return {route: 'payments/purchase', method: 'post'}
        case flowOptions.GROUP_SESSION:
        case flowOptions.DIRECT_GROUP_SESSION:
        case flowOptions.STAFF_APPOINTMENT:
        case flowOptions.SPACE_APPOINTMENT:
        case flowOptions.DIRECT_STAFF_APPOINTMENT:
        case flowOptions.DIRECT_SPACE_APPOINTMENT:
            if(loggedIn) {
                return {route: 'scheduleUser/insert', method: 'post'}
            } else {
                return {route: 'registerUser', method: 'post'}
            }
        case flowOptions.COURSE:
        case flowOptions.DIRECT_COURSE:
            const {route} = getInfoForCourseBooking(values)
            return {route: route, method: 'post'}
        case flowOptions.PRODUCT_PURCHASE:
        case flowOptions.DIRECT_PRODUCT_PURCHASE:
            if(loggedIn) {
                return {route: 'payments/purchase', method: 'post'}
            } else {
                return {route: 'registerUser', method: 'post'}
            }
        case flowOptions.DIRECT_TRIAL_GROUP:
            return {route: 'registerUser', method: 'post'}
        case flowOptions.KIOSK_CLOSE_DEBT:
        case flowOptions.KIOSK_CLOSE_ALL_DEBTS:
            return {route: 'kiosk/closeMembershipUserDebt', method: 'post'}
        default:
            return {};
    }
}

//</editor-fold>